import React, { useEffect, useRef } from "react";
import * as d3 from "d3";

const BarPlotHorizontal = () => {
  const chartRef = useRef();

  useEffect(() => {
    // Updated Data
    const data = [
      { name: "GOLD SALE", weight: 1970, amount: 90 },
      { name: "SILVER SALE", weight: 93, amount: 9232320 },
      { name: "DIAMOND SALE", weight: 12323210, amount: 946540 },
      { name: "OLD GOLD", weight: 10, amount: 9232320 },
      { name: "OLD SILVER", weight: 2323230, amount: 90 },
      { name: "CUSTOMER ORDER", weight: 40, amount: 232390 },
      { name: "REPAIR", weight: 5650, amount: 90 },
      { name: "RETURN", weight: 80, amount: 997880 },
      { name: "EXPENSES", weight: 243430, amount: 90 },
      { name: "FINE PAID", weight: 90, amount: 23290 },
    ];

    // Clear any existing SVG
    d3.select(chartRef.current).select("svg").remove();

    // Get container dimensions
    const container = chartRef.current.getBoundingClientRect();
    const width = container.width;
    const height = container.height;

    // Set margins
    const margin = { top: 5, right: 5, bottom: 5, left: 5 };
    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;

    // Create SVG container
    const svg = d3
      .select(chartRef.current)
      .append("svg")
      .attr("width", width)
      .attr("height", height)
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    // Scales
    const totalWeight = d3.sum(data, (d) => d.weight);
    const totalAmount = d3.sum(data, (d) => d.amount);

    const xScale = d3
      .scaleLinear()
      .domain([0, 1]) // Percentage of the width
      .range([0, innerWidth]);

    const yScale = d3
      .scaleBand()
      .domain(data.map((d) => d.name))
      .range([0, innerHeight])
      .padding(0.2);

    // Colors
    const colors = { weight: "#87cefa", amount: "#dda0dd" };

    // Function to measure text width
    const measureTextWidth = (text) => {
      const textMeasure = svg
        .append("text")
        .attr("class", "temp-text")
        .style("font-size", "12px")
        .style("font-weight", "bold")
        .text(text);
      const width = textMeasure.node().getBBox().width;
      textMeasure.remove();
      return width;
    };

    // Calculate the maximum width for the longest name
    const maxNameWidth = d3.max(
      data.map((d) => measureTextWidth(d.name))
    ) + 10; // Adding padding for name
    const redWidthPercentage = xScale.invert(maxNameWidth);

    // Define the minimum width for weight and amount bars
    const minBarWidth = 20;

    // Add groups for each bar
    const groups = svg
      .selectAll("g.bar-group")
      .data(data)
      .enter()
      .append("g")
      .attr("transform", (d) => `translate(0, ${yScale(d.name)})`);

    // Add red bar with fixed width for name
    groups
      .append("rect")
      .attr("x", 0)
      .attr("width", maxNameWidth) // Fixed width for name
      .attr("height", yScale.bandwidth())
      .attr("fill", "#fa8072");

    // Add label text on the red bar
    groups
      .append("text")
      .attr("x", 5) // Slight padding within the red bar
      .attr("y", yScale.bandwidth() / 2)
      .attr("dy", "0.35em")
      .text((d) => d.name)
      .attr("fill", "white")
      .style("font-size", "10px")
      .style("font-weight", "bold");

    // Calculate remaining width after red bar
    const remainingWidth = innerWidth - maxNameWidth;

    // Calculate the proportional widths for weight and amount bars
    const totalWeightAmount = totalWeight + totalAmount;

    // Add weight bar with dynamic width
    groups
      .append("rect")
      .attr("x", maxNameWidth) // Start from the end of the red bar
      .attr(
        "width",
        (d) =>
          Math.max(
            (remainingWidth * d.weight) / totalWeightAmount,
            measureTextWidth(d.weight) + 10, // Ensure bar is wide enough to fit the text
            minBarWidth
          )
      )
      .attr("height", yScale.bandwidth())
      .attr("fill", colors.weight);

    // Add value for weight bar
    groups
      .append("text")
      .attr(
        "x",
        (d) =>
          maxNameWidth +
          Math.max(
            (remainingWidth * d.weight) / totalWeightAmount,
            measureTextWidth(d.weight) + 10,
            minBarWidth
          ) / 2
      )
      .attr("y", yScale.bandwidth() / 2)
      .attr("dy", "0.35em")
      .text((d) => d.weight)
      .attr("fill", "black")
      .style("font-size", "12px")
      .style("font-weight", "bold")
      .attr("text-anchor", "middle");

    // Add amount bar with dynamic width
    groups
      .append("rect")
      .attr(
        "x",
        (d) =>
          maxNameWidth +
          Math.max(
            (remainingWidth * d.weight) / totalWeightAmount,
            measureTextWidth(d.weight) + 10,
            minBarWidth
          )
      )
      .attr(
        "width",
        (d) =>
          Math.max(
            (remainingWidth * d.amount) / totalWeightAmount,
            measureTextWidth(d.amount) + 10, // Ensure bar is wide enough to fit the text
            minBarWidth
          )
      )
      .attr("height", yScale.bandwidth())
      .attr("fill", colors.amount);

    // Add value for amount bar
    groups
      .append("text")
      .attr(
        "x",
        (d) =>
          maxNameWidth +
          Math.max(
            (remainingWidth * d.weight) / totalWeightAmount,
            measureTextWidth(d.weight) + 10,
            minBarWidth
          ) +
          Math.max(
            (remainingWidth * d.amount) / totalWeightAmount,
            measureTextWidth(d.amount) + 10,
            minBarWidth
          ) / 2
      )
      .attr("y", yScale.bandwidth() / 2)
      .attr("dy", "0.35em")
      .text((d) => d.amount)
      .attr("fill", "black")
      .style("font-size", "12px")
      .style("font-weight", "bold")
      .attr("text-anchor", "middle");
  }, []);

  return <div ref={chartRef} style={{ width: "100%", height: "100%" }}></div>;
};

export default BarPlotHorizontal;
